import { useMemo, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Avatar,
  Button,
  ConfirmDialog,
  Input,
  Modal,
  PageHeader,
  RowMenu,
  Select,
  StatusBadge,
  Switch,
  initialsFromName,
  type SelectOption,
} from '../../components';
import { ApiError, apiRequest } from '../../lib/api';
import { useAuthStore } from '../../lib/auth-store';
import { useCrud, type ListResponse } from '../../lib/useCrud';

type UserRow = {
  id: string;
  email: string;
  full_name: string;
  phone: string | null;
  job_title: string | null;
  profile_photo_url: string | null;
  department_id: string | null;
  shift_id: string | null;
  is_active: boolean;
  last_login_at: string | null;
  roles: string[];
};

type CreateBody = {
  email: string;
  password: string;
  full_name: string;
  phone?: string | null;
  job_title?: string | null;
  department_id?: string | null;
  shift_id?: string | null;
  is_active?: boolean;
  roles?: string[];
};
type UpdateBody = {
  full_name?: string;
  phone?: string | null;
  job_title?: string | null;
  department_id?: string | null;
  shift_id?: string | null;
};

export function StaffPage() {
  const currentUser = useAuthStore((s) => s.user);

  const crud = useCrud<UserRow, CreateBody, UpdateBody>({
    key: 'users',
    path: '/users',
    listQuery: 'include_inactive=true&limit=100',
  });

  const departmentsQ = useQuery({
    queryKey: ['hrm-departments', 'lookup'],
    queryFn: () =>
      apiRequest<ListResponse<{ id: string; name: string }>>('/hrm/departments?limit=100'),
  });
  const shiftsQ = useQuery({
    queryKey: ['hrm-shifts', 'lookup'],
    queryFn: () =>
      apiRequest<ListResponse<{ id: string; name: string }>>('/hrm/shifts?limit=100'),
  });
  const rolesQ = useQuery({
    queryKey: ['hrm-roles', 'lookup'],
    queryFn: () =>
      apiRequest<ListResponse<{ id: string; slug: string; name: string }>>('/hrm/roles?limit=100'),
  });

  const departmentOptions: SelectOption[] = useMemo(
    () => (departmentsQ.data?.data ?? []).map((d) => ({ value: d.id, label: d.name })),
    [departmentsQ.data],
  );
  const shiftOptions: SelectOption[] = useMemo(
    () => (shiftsQ.data?.data ?? []).map((s) => ({ value: s.id, label: s.name })),
    [shiftsQ.data],
  );
  const roleOptions = useMemo(
    () => rolesQ.data?.data ?? [],
    [rolesQ.data],
  );

  const [editing, setEditing] = useState<UserRow | 'new' | null>(null);
  const [resetting, setResetting] = useState<UserRow | null>(null);
  const [rolesTarget, setRolesTarget] = useState<UserRow | null>(null);
  const [toggling, setToggling] = useState<UserRow | null>(null);
  const [togglingLoading, setTogglingLoading] = useState(false);

  const deptNameById = new Map(departmentOptions.map((d) => [d.value, d.label]));
  const shiftNameById = new Map(shiftOptions.map((s) => [s.value, s.label]));

  async function handleToggle(user: UserRow) {
    setTogglingLoading(true);
    try {
      await apiRequest(`/users/${user.id}/${user.is_active ? 'deactivate' : 'activate'}`, {
        method: 'POST',
      });
      crud.invalidate();
      setToggling(null);
    } finally {
      setTogglingLoading(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Staff"
        subtitle="Internal users, roles, and access."
        actions={
          <Button variant="primary" icon="userPlus" onClick={() => setEditing('new')}>
            New user
          </Button>
        }
      />

      {crud.list.isLoading && <div className="card card-body muted">Loading…</div>}
      {crud.list.error && <div className="alert">{(crud.list.error as Error).message}</div>}
      {crud.list.data && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 44 }} />
                <th>Name</th>
                <th>Email</th>
                <th>Roles</th>
                <th>Department</th>
                <th>Shift</th>
                <th>Status</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {crud.list.data.data.map((row) => {
                const isSelf = currentUser?.id === row.id;
                return (
                  <tr key={row.id}>
                    <td>
                      <Avatar
                        initials={initialsFromName(row.full_name)}
                        color="var(--green-600)"
                        size="sm"
                      />
                    </td>
                    <td>
                      {row.full_name}
                      {isSelf && (
                        <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>
                          (you)
                        </span>
                      )}
                    </td>
                    <td className="cell-mono">{row.email}</td>
                    <td>
                      {row.roles.length === 0 ? (
                        <span className="muted">—</span>
                      ) : (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {row.roles.map((r) => (
                            <span key={r} className="badge gray">
                              {r}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="muted">
                      {row.department_id ? (deptNameById.get(row.department_id) ?? '—') : '—'}
                    </td>
                    <td className="muted">
                      {row.shift_id ? (shiftNameById.get(row.shift_id) ?? '—') : '—'}
                    </td>
                    <td>
                      <StatusBadge active={row.is_active} />
                    </td>
                    <td>
                      <RowMenu
                        items={[
                          { icon: 'edit', label: 'Edit profile', onClick: () => setEditing(row) },
                          { icon: 'tag', label: 'Manage roles', onClick: () => setRolesTarget(row) },
                          {
                            icon: 'sparkle',
                            label: 'Reset password',
                            onClick: () => setResetting(row),
                          },
                          'divider',
                          ...(isSelf
                            ? []
                            : [
                                {
                                  icon: row.is_active ? ('logout' as const) : ('check' as const),
                                  label: row.is_active ? 'Deactivate' : 'Activate',
                                  onClick: () => setToggling(row),
                                  danger: row.is_active,
                                },
                              ]),
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <UserEditModal
          row={editing === 'new' ? undefined : editing}
          departmentOptions={departmentOptions}
          shiftOptions={shiftOptions}
          roleOptions={roleOptions}
          onClose={() => setEditing(null)}
          onSubmit={async (payload, mode) => {
            if (mode === 'create') {
              await crud.create.mutateAsync(payload as CreateBody);
            } else {
              await crud.update.mutateAsync({
                id: (editing as UserRow).id,
                body: payload as UpdateBody,
              });
            }
            setEditing(null);
          }}
        />
      )}

      {resetting && (
        <PasswordResetModal
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={() => {
            crud.invalidate();
            setResetting(null);
          }}
        />
      )}

      {rolesTarget && (
        <RolesModal
          user={rolesTarget}
          roleOptions={roleOptions}
          onClose={() => setRolesTarget(null)}
          onDone={() => {
            crud.invalidate();
            setRolesTarget(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(toggling)}
        title={toggling?.is_active ? 'Deactivate user' : 'Activate user'}
        message={
          toggling ? (
            <>
              {toggling.is_active
                ? `Deactivate ${toggling.full_name}? Their active sessions will be invalidated.`
                : `Activate ${toggling.full_name}?`}
            </>
          ) : null
        }
        destructive={toggling?.is_active}
        confirmLabel={toggling?.is_active ? 'Deactivate' : 'Activate'}
        loading={togglingLoading}
        onCancel={() => setToggling(null)}
        onConfirm={() => toggling && handleToggle(toggling)}
      />
    </>
  );
}

/* ───────────── modals ───────────── */

type UserFormState = {
  email: string;
  password: string;
  full_name: string;
  phone: string;
  job_title: string;
  department_id: string;
  shift_id: string;
  is_active: boolean;
  roles: string[];
};

function UserEditModal({
  row,
  departmentOptions,
  shiftOptions,
  roleOptions,
  onClose,
  onSubmit,
}: {
  row?: UserRow;
  departmentOptions: SelectOption[];
  shiftOptions: SelectOption[];
  roleOptions: Array<{ id: string; slug: string; name: string }>;
  onClose: () => void;
  onSubmit: (
    payload: CreateBody | UpdateBody,
    mode: 'create' | 'update',
  ) => Promise<void>;
}) {
  const mode: 'create' | 'update' = row ? 'update' : 'create';
  const [form, setForm] = useState<UserFormState>(() => ({
    email: row?.email ?? '',
    password: '',
    full_name: row?.full_name ?? '',
    phone: row?.phone ?? '',
    job_title: row?.job_title ?? '',
    department_id: row?.department_id ?? '',
    shift_id: row?.shift_id ?? '',
    is_active: row?.is_active ?? true,
    roles: row?.roles ?? ['staff'],
  }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleRole(slug: string) {
    setForm((f) =>
      f.roles.includes(slug)
        ? { ...f, roles: f.roles.filter((r) => r !== slug) }
        : { ...f, roles: [...f.roles, slug] },
    );
  }

  async function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'create') {
        const body: CreateBody = {
          email: form.email.trim(),
          password: form.password,
          full_name: form.full_name.trim(),
          phone: form.phone.trim() || null,
          job_title: form.job_title.trim() || null,
          department_id: form.department_id || null,
          shift_id: form.shift_id || null,
          is_active: form.is_active,
          roles: form.roles.length > 0 ? form.roles : ['staff'],
        };
        await onSubmit(body, 'create');
      } else {
        const body: UpdateBody = {
          full_name: form.full_name.trim(),
          phone: form.phone.trim() || null,
          job_title: form.job_title.trim() || null,
          department_id: form.department_id || null,
          shift_id: form.shift_id || null,
        };
        await onSubmit(body, 'update');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={mode === 'create' ? 'New user' : 'Edit user'} width={560}>
      <form onSubmit={handle}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input
                id="user-name"
                label="Full name"
                value={form.full_name}
                onChange={(v) => setForm({ ...form, full_name: v })}
                required
                autoFocus
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input
                id="user-jobtitle"
                label="Job title"
                value={form.job_title}
                onChange={(v) => setForm({ ...form, job_title: v })}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input
                id="user-email"
                label="Email"
                type="email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                required
                disabled={mode === 'update'}
              />
              {mode === 'update' && (
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                  Email cannot be changed after creation.
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <Input
                id="user-phone"
                label="Phone"
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
              />
            </div>
          </div>

          {mode === 'create' && (
            <Input
              id="user-password"
              label="Password (min 12 chars)"
              type="password"
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              minLength={12}
              required
            />
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Select
                id="user-dept"
                label="Department"
                placeholder="— none —"
                value={form.department_id}
                options={departmentOptions}
                onChange={(v) => setForm({ ...form, department_id: v })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Select
                id="user-shift"
                label="Shift"
                placeholder="— none —"
                value={form.shift_id}
                options={shiftOptions}
                onChange={(v) => setForm({ ...form, shift_id: v })}
              />
            </div>
          </div>

          {mode === 'create' && (
            <div>
              <div className="field-label">Roles</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {roleOptions.map((r) => {
                  const on = form.roles.includes(r.slug);
                  return (
                    <button
                      key={r.slug}
                      type="button"
                      className={`pill ${on ? 'active' : ''}`}
                      onClick={() => toggleRole(r.slug)}
                    >
                      {r.name}
                    </button>
                  );
                })}
              </div>
              <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                Use "Manage roles" on the row to change roles after creation.
              </div>
            </div>
          )}

          {mode === 'create' && (
            <div className="row">
              <Switch
                on={form.is_active}
                onChange={(v) => setForm({ ...form, is_active: v })}
                label="Active"
              />
              <span className="muted">Active on creation</span>
            </div>
          )}

          {error && <div className="alert">{error}</div>}
        </div>
        <div
          className="card-header"
          style={{ justifyContent: 'flex-end', borderTop: '1px solid var(--border)', borderBottom: 'none' }}
        >
          <Button onClick={onClose} disabled={submitting} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function PasswordResetModal({
  user,
  onClose,
  onDone,
}: {
  user: UserRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handle(e: FormEvent) {
    e.preventDefault();
    if (pw !== pw2) {
      setError('Passwords do not match');
      return;
    }
    if (pw.length < 12) {
      setError('Password must be at least 12 characters');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiRequest(`/users/${user.id}/password-reset`, {
        method: 'POST',
        body: { password: pw },
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Reset failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={`Reset password — ${user.full_name}`} width={440}>
      <form onSubmit={handle}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="muted" style={{ fontSize: 13 }}>
            Active sessions for this user will be invalidated.
          </div>
          <Input
            id="pw1"
            label="New password (min 12 chars)"
            type="password"
            value={pw}
            onChange={setPw}
            minLength={12}
            required
            autoFocus
          />
          <Input
            id="pw2"
            label="Confirm password"
            type="password"
            value={pw2}
            onChange={setPw2}
            minLength={12}
            required
          />
          {error && <div className="alert">{error}</div>}
        </div>
        <div
          className="card-header"
          style={{ justifyContent: 'flex-end', borderTop: '1px solid var(--border)', borderBottom: 'none' }}
        >
          <Button onClick={onClose} disabled={submitting} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Resetting…' : 'Reset password'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function RolesModal({
  user,
  roleOptions,
  onClose,
  onDone,
}: {
  user: UserRow;
  roleOptions: Array<{ id: string; slug: string; name: string }>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [selected, setSelected] = useState<string[]>(user.roles);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(slug: string) {
    setSelected((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]));
  }

  async function handle() {
    if (selected.length === 0) {
      setError('Pick at least one role');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiRequest(`/users/${user.id}/roles`, {
        method: 'PUT',
        body: { roles: selected },
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Role update failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={`Roles — ${user.full_name}`} width={440}>
      <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {roleOptions.map((r) => {
            const on = selected.includes(r.slug);
            return (
              <button
                key={r.slug}
                type="button"
                className={`pill ${on ? 'active' : ''}`}
                onClick={() => toggle(r.slug)}
              >
                {r.name}
              </button>
            );
          })}
        </div>
        {error && <div className="alert">{error}</div>}
      </div>
      <div
        className="card-header"
        style={{ justifyContent: 'flex-end', borderTop: '1px solid var(--border)', borderBottom: 'none' }}
      >
        <Button onClick={onClose} disabled={submitting} type="button">
          Cancel
        </Button>
        <Button variant="primary" type="button" disabled={submitting} onClick={handle}>
          {submitting ? 'Saving…' : 'Save roles'}
        </Button>
      </div>
    </Modal>
  );
}
